import { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Alert, Spinner } from 'react-bootstrap';
import { loadStripe } from '@stripe/stripe-js';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { getSafeSession, getCachedSession, getCachedProfile, setCachedProfile } from '../../supabaseClient';
import { toast } from 'react-toastify';
import NavBar from './Navbar';
import './PricingPlans.css';

let stripePromiseCache = null;

const getStripe = async () => {
  if (stripePromiseCache) {
    return stripePromiseCache;
  }

  let pubKey = null;

  try {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/stripe-publishable-key`,
      { timeout: 10000 }
    );
    pubKey = response.data?.publishableKey;
  } catch (err) {
    console.warn("Failed to fetch Stripe publishable key from backend, falling back to env:", err);
  }

  if (!pubKey) {
    pubKey = process.env.REACT_APP_STRIPE_PUBLIC_KEY;
  }

  if (pubKey) {
    stripePromiseCache = loadStripe(pubKey);
    return stripePromiseCache;
  }

  return null;
};

const PricingPlans = () => {
  const [loading, setLoading] = useState(false);
  const [fetchingPlans, setFetchingPlans] = useState(true);
  const [plans, setPlans] = useState([]);
  const [error, setError] = useState(null);
  const [userProfile, setUserProfile] = useState(() => {
    const s = getCachedSession();
    return s?.user?.id ? getCachedProfile(s.user.id) : null;
  });
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const { data: { session } } = await getSafeSession(15000);
        if (session) {
          const response = await axios.get(
            `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/user/profile`,
            {
              headers: {
                Authorization: `Bearer ${session.access_token}`
              },
              timeout: 10000
            }
          );
          if (response.data) {
            setUserProfile(response.data);
            setCachedProfile(session.user.id, response.data);
          }
        }
      } catch (err) {
        console.warn("Failed to fetch profile in pricing page:", err);
      }
    };

    const fetchPlans = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/plans`,
          { timeout: 10000 }
        );
        setPlans(response.data);
      } catch (err) {
        console.error("Failed to fetch pricing plans:", err);
        setError("Could not load pricing plans. Please try again later.");
      } finally {
        setFetchingPlans(false);
      }
    };

    fetchProfile();
    fetchPlans();
  }, []);

  const handlePlanSelect = async (planName) => {
    if ((planName || '').toLowerCase().includes('expansion')) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data: { session } } = await getSafeSession(15000);

      if (!session) {
        navigate('/auth');
        return;
      }

      const isPaid = userProfile && ['growth', 'expansion', 'agency'].includes((userProfile.plan || '').toLowerCase());

      if (isPaid) {
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/update-subscription-plan`,
          { plan: planName },
          {
            headers: {
              Authorization: `Bearer ${session.access_token}`,
              'Content-Type': 'application/json'
            },
            timeout: 15000
          }
        );

        if (response.data && response.data.status === 'success') {
          toast.success(response.data.message || `Plan successfully updated to ${planName}!`);
          navigate('/account');
          return;
        } else {
          throw new Error("Could not update subscription plan.");
        }
      }

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/create-subscription-session`,
        { plan: planName },
        {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            'Content-Type': 'application/json'
          },
          timeout: 15000
        }
      );

      const stripe = await Promise.race([
        getStripe(),
        new Promise((_, reject) => setTimeout(() => reject(new Error("Stripe checkout integration failed to load")), 10000))
      ]);

      if (!stripe) {
        throw new Error("Stripe integration failed to initialize. Please check your network or try again.");
      }

      const { error: stripeError } = await stripe.redirectToCheckout({
        sessionId: response.data.id
      });

      if (stripeError) throw stripeError;

    } catch (err) {
      console.error("Checkout/Update failed:", err);
      setError(err.response?.data?.detail || err.message || "Plan update failed");
      toast.error(err.response?.data?.detail || err.message || "Plan update failed");
    } finally {
      setLoading(false);
    }
  };

  const userPlanKey = (userProfile?.plan || "").toLowerCase();

  const getPlanDetails = (plan) => {
    const nameLower = (plan.name || "").toLowerCase();
    const isAgency = nameLower.includes('agency');
    const isCore = nameLower.includes('core') || nameLower.includes('guest');
    const isGrowth = nameLower.includes('growth') && !isAgency;
    const isExpansion = nameLower.includes('expansion');

    if (isCore) {
      return {
        tier: 'TIER 01',
        subtitle: 'Try the platform with a free AI readiness scan',
        cta: 'Run Free Scan',
        variant: 'outline-light',
        action: () => {
          if (!userProfile) {
            navigate('/auth');
          } else {
            navigate('/scanner');
          }
        },
        disabled: false
      };
    } else if (isGrowth) {
      const isCurrent = userPlanKey === 'growth';
      return {
        tier: 'TIER 02',
        subtitle: 'Everything you need to measure and improve AI readiness.',
        cta: isCurrent ? 'Current Plan' : (loading ? 'Processing...' : 'Get started'),
        variant: 'primary',
        action: () => handlePlanSelect('growth'),
        disabled: isCurrent
      };
    } else if (isExpansion) {
      const isCurrent = userPlanKey === 'expansion';
      return {
        tier: 'TIER 04',
        subtitle: 'Unlock advanced AI optimization and grow your visibility.',
        cta: isCurrent ? 'Current Plan' : 'Coming Soon',
        variant: 'outline-light',
        action: () => { },
        disabled: true
      };
    } else if (isAgency) {
      const isCurrent = userPlanKey === 'agency';
      return {
        tier: 'TIER 03',
        subtitle: 'For agencies managing multiple clients (up to 20 client websites).',
        cta: isCurrent ? 'Current Plan' : (loading ? 'Processing...' : 'Get started'),
        variant: 'primary',
        action: () => handlePlanSelect('agency'),
        disabled: isCurrent
      };
    }

    return {
      tier: 'PLAN',
      subtitle: '',
      cta: 'Subscribe',
      variant: 'primary',
      action: () => handlePlanSelect(plan.name),
      disabled: false
    };
  };

  const getPlanPricing = (plan) => {
    const nameLower = (plan.name || "").toLowerCase();
    const isAgency = nameLower.includes('agency');
    const isCore = nameLower.includes('core') || nameLower.includes('guest');
    const isGrowth = nameLower.includes('growth') && !isAgency;
    const isExpansion = nameLower.includes('expansion');

    if (isCore) {
      return {
        type: 'standard',
        originalPrice: null,
        badge: null,
        currency: '$',
        price: '0',
        unit: '/month',
        subtext: 'Free forever'
      };
    } else if (isGrowth) {
      return {
        type: 'standard',
        originalPrice: '129.99',
        badge: 'LAUNCH YEAR',
        currency: '$',
        price: '79.99',
        unit: '/month',
        subtext: 'Billed monthly • Cancel anytime'
      };
    } else if (isAgency) {
      return {
        type: 'standard',
        originalPrice: '19.99',
        badge: 'LAUNCH YEAR',
        currency: '$',
        price: '9.99',
        unit: '/business/month',
        subtext: 'First business $79.99/month • Cancel anytime'
      };
    } else if (isExpansion) {
      return {
        type: 'coming_soon',
        title: 'Coming soon',
        subtext: 'More power, more flexibility. Stay tuned for updates.'
      };
    }

    return {
      type: 'standard',
      originalPrice: null,
      badge: null,
      currency: '$',
      price: plan.price ? (plan.price / 100).toFixed(2) : '0',
      unit: '/month',
      subtext: ''
    };
  };

  // Parser for features
  const renderFeatureItem = (feature, index) => {
    if (feature.startsWith('group:')) {
      const groupName = feature.replace('group:', '').trim();
      return (
        <div key={index} className="feature-group-header">
          {groupName}
        </div>
      );
    }

    if (feature === '[MOCKUP]') {
      return (
        <div key={index} className="scanner-mockup-wrapper">
          <div className="mini-mockup-container">
            {/* Left Panel: Scanner */}
            <div className="mini-mockup-panel left-panel">
              <div className="mockup-header-text">AI Visibility Scanner</div>
              <div className="mockup-label">Website URL</div>
              <div className="mockup-input">https://example.com</div>
              <div className="mockup-label">Company Name</div>
              <div className="mockup-input">Company Name</div>
              <div className="mockup-label">Company Information</div>
              <div className="mockup-textarea">What does your company do?</div>
              <div className="mockup-btn">SCAN</div>
            </div>

            {/* Right Panel: Score */}
            <div className="mini-mockup-panel right-panel">
              <div className="mockup-header-text score-text">AI Visibility Score: <span className="score-value">84/100</span></div>
              <div className="mockup-section-title error-title">WHY THIS SCORE?</div>
              <div className="mockup-bullet error-bullet">No local business schemas indicators found in website body.</div>
              <div className="mockup-bullet error-bullet">No sync/inconsistent brand info found on search.</div>

              <div className="mockup-section-title check-title">RECOMMENDED IMPROVEMENTS</div>
              <div className="mockup-bullet check-bullet">Add local business schemas.</div>
              <div className="mockup-bullet check-bullet font-small">Verify brand info across search.</div>
            </div>
          </div>
        </div>
      );
    }

    if (feature.includes('|')) {
      const parts = feature.split('|');
      const title = parts[0].trim();

      if (parts[1]?.trim() === 'COMING SOON') {
        const description = parts[2]?.trim();
        return (
          <li key={index} className="feature-item-complex">
            <div className="feature-item-header">
              <span className="checkmark-circle">✓</span>
              <span className="feature-title">{title}</span>
              <span className="coming-soon-badge">COMING SOON</span>
            </div>
            {description && <div className="feature-description">{description}</div>}
          </li>
        );
      } else {
        const description = parts[1].trim();
        return (
          <li key={index} className="feature-item-complex">
            <div className="feature-item-header">
              <span className="checkmark-circle">✓</span>
              <span className="feature-title">{title}</span>
            </div>
            <div className="feature-description">{description}</div>
          </li>
        );
      }
    }

    return (
      <li key={index} className="feature-item-simple">
        <span className="checkmark-circle">✓</span>
        <span className="feature-text">{feature}</span>
      </li>
    );
  };

  return (
    <div className="pricing-page-wrapper">
      <NavBar />
      <section className="pricing-plans py-5" id="pricing-plans">
        <Container>
          {error && (
            <Alert variant="danger" onClose={() => setError(null)} dismissible>
              {error}
            </Alert>
          )}

          <div className="text-center pricing-header-section">
            <span className="pricing-section-badge">PRICING PLANS</span>
            <h2 className="pricing-section-title mt-3 mb-3">
              Simple Pricing for Every Business Need
            </h2>
            <p className="pricing-section-subtitle">
              Start small and upgrade as you grow. No contracts, cancel anytime.
            </p>
          </div>

          {fetchingPlans ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="cyan" />
              <p className="text-muted mt-2">Loading pricing plans...</p>
            </div>
          ) : (
            <Row className="g-4 justify-content-center pricing-cards-row">
              {[...plans].sort((a, b) => {
                const getOrder = (name) => {
                  const n = (name || "").toLowerCase();
                  if (n.includes('guest') || n.includes('core')) return 1;
                  if (n.includes('growth') && !n.includes('agency')) return 2;
                  if (n.includes('agency')) return 3;
                  if (n.includes('expansion')) return 4;
                  return 5;
                };
                return getOrder(a.name) - getOrder(b.name);
              }).map((plan) => {
                const details = getPlanDetails(plan);
                const pricing = getPlanPricing(plan);
                const isGrowth = (plan.name || "").toLowerCase().includes('growth');

                return (
                  <Col key={plan.id} lg={3} md={6} className="d-flex">
                    <Card className={`premium-pricing-card w-100 ${isGrowth ? 'featured-glowing-card' : ''}`}>
                      {isGrowth && (
                        <div className="glowing-border-top"></div>
                      )}

                      <Card.Body className="p-4 d-flex flex-column card-inner-content">
                        <div className="tier-tag">{details.tier}</div>
                        <h3 className="premium-plan-name">{plan.name}</h3>
                        <p className="premium-plan-subtitle">{details.subtitle}</p>

                        {/* Render Price */}
                        <div className="premium-plan-pricing-container">
                          {pricing.type === 'coming_soon' ? (
                            <div className="t-expansion-pricing">
                             
                              <div className="t-expansion-title">{pricing.title}</div>
                              <div className="t-expansion-sub">{pricing.subtext}</div>
                            </div>
                          ) : (
                            <>
                              <div className="t-price-row">
                                {pricing.originalPrice && (
                                  <div className="t-price-orig">
                                    <span className="t-strike-val">${pricing.originalPrice}</span>
                                  </div>
                                )}
                                <div className="t-price-main-wrap">
                                  {pricing.badge && (
                                    <span className="t-pbadge">{pricing.badge}</span>
                                  )}
                                  <div className="t-price-amount">
                                    <span className="t-pcurr">{pricing.currency}</span>
                                    <span className="t-pval">{pricing.price}</span>
                                    {pricing.unit && <span className="t-punit">{pricing.unit}</span>}
                                  </div>
                                </div>
                              </div>
                              {pricing.subtext && (
                                <div className="t-pper">{pricing.subtext}</div>
                              )}
                            </>
                          )}
                        </div>

                        <button
                          className={`premium-cta-btn ${details.variant === 'primary' ? 'gradient-btn' : 'outline-btn'}`}
                          onClick={details.action}
                          disabled={loading || details.disabled}
                        >
                          {details.cta}
                        </button>

                        <ul className="premium-feature-list mt-4">
                          {plan.features
                            .filter(f => f !== '[MOCKUP]')
                            .map((feature, i) => renderFeatureItem(feature, i))}
                        </ul>

                        {/* Always render scanner mockup at the bottom of Core tier */}
                        {details.tier === 'TIER 01' && (
                          <div className="scanner-mockup-wrapper">
                            <div className="mini-mockup-container">
                              {/* Left Panel: Scanner */}
                              <div className="mini-mockup-panel left-panel">
                                <div className="mockup-header-text">AI Visibility Scanner</div>
                                <div className="mockup-label">Website URL</div>
                                <div className="mockup-input">https://example.com</div>
                                <div className="mockup-label">Company Name</div>
                                <div className="mockup-input">Company Name</div>
                                <div className="mockup-label">Company Information</div>
                                <div className="mockup-textarea">What does your company do?</div>
                                <div className="mockup-btn">SCAN</div>
                              </div>

                              {/* Right Panel: Score */}
                              <div className="mini-mockup-panel right-panel">
                                <div className="mockup-header-text score-text">AI Visibility Score: <span className="score-value">84/100</span></div>
                                <div className="mockup-section-title error-title">WHY THIS SCORE?</div>
                                <div className="mockup-bullet error-bullet">No local business schemas indicators found in website body.</div>
                                <div className="mockup-bullet error-bullet">No sync/inconsistent brand info found on search.</div>

                                <div className="mockup-section-title check-title">RECOMMENDED IMPROVEMENTS</div>
                                <div className="mockup-bullet check-bullet">Add local business schemas.</div>
                                <div className="mockup-bullet check-bullet font-small">Verify brand info across search.</div>
                              </div>
                            </div>
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          )}

          <div className="text-center mt-5 guarantee-container">
            <p className="guarantee-text">
              <i className="fas fa-lock-open me-2"></i> All plans include a 30-day money back guarantee
            </p>
          </div>
        </Container>
      </section>
    </div>
  );
};

export default PricingPlans;